import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { GridVisao } from '@gestor-web/@core/api/ords/model';
import { FUNCIONALIDADE } from '@gestor-web/@core/auth/funcionalidades';
import { GeralService, PermissaoService } from '@gestor-web/app/shared/services';
import { GridService } from '@gestor-web/app/shared/services/grid.service';
import { AgGridAngular } from 'ag-grid-angular';
import { environment } from '@gestor-web/environments/environment';
import { Subject, Subscription, takeUntil } from 'rxjs';

@Component({
    selector: 'ag-views-toolbar',
    templateUrl: './ag-views-toolbar.component.html',
    styleUrls: ['./ag-views-toolbar.component.scss'],
    standalone: false
})
export class AgViewsToolbarComponent implements OnInit, OnChanges, OnDestroy {
  readonly ORIENTACOES_EDIT_URL = `${environment.API_APEX_BASE_PATH}/gestor-idss-admin/orientacao`;
  orientacoesEditUrl: string = this.ORIENTACOES_EDIT_URL;

  @Input() grid: AgGridAngular;
  @Input() gridKey: string;

  @Output() onChange = new EventEmitter<GridVisao>();

  podeEditarOrientacoes = false;

  visoesSub: Subscription;
  syncSub: Subscription;

  visoes: GridVisao[];
  visoesFixas: GridVisao[];
  visoesNaoFixas: GridVisao[];

  visaoSelecionada: GridVisao;

  viewTooltipKey: string;
  viewTooltipHtml: string;
  viewTooltipUrl: string;

  constructor(
    private service: GridService,
    private geralService: GeralService,
    private permissaoService: PermissaoService,
  ) {}

  ngOnInit(): void {
    this.permissaoService
      .checkAcessoFeature(FUNCIONALIDADE.EDITAR_ORIENTACOES)
      .pipe(takeUntil(this.ngDestroyed$))
      .subscribe(pode => (this.podeEditarOrientacoes = pode));
  }

  ngOnChanges(changes: SimpleChanges): void {
    // console.log('[ViewToolbar] changes', changes);

    if (changes['gridKey']) {
      // const key = changes['gridKey'];
      // console.log('[ViewToolbar] changes[gridKey]', key.currentValue, '<-', key.previousValue);
      this.resubscribeVisoes();
      this.reapplyLastSimilarVision();
    }

    if (changes['grid']) {
      if (!this.grid || !this.gridKey) {
        return;
      }
      /*
      const grid = changes['grid'];
      console.log('[ViewToolbar] changes[grid]', grid.currentValue, '<-', grid.previousValue, ' - key:', this.gridKey);
      console.log('[ViewToolbar] grid exists?', this.grid);
      console.log('[ViewToolbar] grid api ready?', !!this.grid?.api);
      console.log('[ViewToolbar] grid options exists?', this.grid?.gridOptions);
      console.log('[ViewToolbar] grid options onGridReady?', this.grid?.gridOptions?.onGridReady);
      */
      const currentVisao = this.service.getCurrentVisao(this.gridKey);
      if (!currentVisao) {
        // console.log('[ViewToolbar] no current vision... apply initial?');
        return;
      }
      const similarVisao = this.service.findVisaoByName(this.gridKey, currentVisao?.nm_visao);
      // console.log('[ViewToolbar] current vision, apply similar?', similarVisao, currentVisao);
      if (similarVisao?.cd_visao != currentVisao?.cd_visao) {
        // console.log('[ViewToolbar] applying visao similar to last grid');
        this.changeVisao(similarVisao);
      } else {
        // console.log('[ViewToolbar] not similar or already applied... skipping');
      }
    }
  }

  private resubscribeVisoes() {
    // console.log('[ViewToolbar] resubscribeVisoes', this.gridKey);
    this.visoesSub?.unsubscribe();
    this.syncSub?.unsubscribe();
    if (!this.gridKey) {
      this.visoes = [];
      return;
    }

    this.visoesSub = this.service
      .getVisoes(this.gridKey)
      .pipe(takeUntil(this.ngDestroyed$))
      .subscribe(visoesData => {
        this.visoes = visoesData.visoes;
        this.visoesFixas = visoesData.fixas;
        this.visoesNaoFixas = visoesData.naoFixas;
      });

    this.syncSub = this.service
      .getVisaoSync(this.gridKey)
      .pipe(takeUntil(this.ngDestroyed$))
      .subscribe(view => {
        if (!view) {
          this.visaoSelecionada = null;
          this.viewTooltipKey = null;
          this.viewTooltipHtml = null;
          this.viewTooltipUrl = null;
          return;
        }

        if (view) {
          this.visaoSelecionada = view;
          this.viewTooltipKey = view.cd_grade + '.' + view.cd_visao;
          this.viewTooltipHtml = view.cd_grade + '.' + view.cd_visao;
          this.geralService.getOrientacao(this.viewTooltipKey).subscribe({
            next: ({ ds_orientacao, ds_url_documentacao }) => {
              this.viewTooltipHtml = ds_orientacao;
              this.viewTooltipUrl = ds_url_documentacao;
              this.orientacoesEditUrl = `${this.ORIENTACOES_EDIT_URL}?p51_cd_orientacao=${encodeURIComponent(
                this.viewTooltipKey,
              )}`;
            },
            error: _ => {
              this.orientacoesEditUrl = this.ORIENTACOES_EDIT_URL;
            },
          });
        }
      });
  }

  private reapplyLastSimilarVision() {
    // console.log('[ViewToolbar] reapplyLastSimilarVision', this.gridKey);

    const lastVisao = this.service.getLastVisao();
    const similarVisao = this.service.findVisaoByName(this.gridKey, lastVisao?.nm_visao);
    if (similarVisao) {
      // console.log('[ViewToolbar] applying visao similar to last gridKey', this.gridKey);
      this.changeVisao(similarVisao);
    }
  }

  private changeVisao(visao: GridVisao) {
    // console.log('[AgViewToolbar] changeVisao', visao);
    if (!this.grid.api) {
      // console.warn('[ViewToolbar] changeVisao api not ready', this.gridKey);
      return;
    }

    this.service.applyVisao(visao, this.grid);

    // Expand all pivots and groups
    const state = this.grid.api.getColumnGroupState();
    const expandedState = state.map(group => ({
      groupId: group.groupId,
      open: true,
    }));
    this.grid.api.setColumnGroupState(expandedState);

    this.onChange.emit(visao);
  }

  private ngDestroyed$ = new Subject<void>();
  ngOnDestroy() {
    // console.log('[ViewToolbar] destroyed', this.gridKey);
    if (this.gridKey) {
      this.service.setVisaoSync(this.gridKey, null);
    }
    this.ngDestroyed$.next();
    this.ngDestroyed$.complete();
  }
}
